import { ZOMBIES } from "./zombies.js";

export type PackSlot = "commander" | "fighter" | "support";

export interface PackMember {
  zombieId: string;
  level: number;
  slot: PackSlot;
}

export interface PackTemplate {
  /** Pack level 1..40 (world threat rings) or hive/boss level. */
  level: number;
  kind: "pack" | "hive" | "boss" | "zone";
  members: PackMember[];
  /** Content-side power estimate, monotonic in level; the exact battle power comes from rules. */
  power: number;
}

export const MAX_PACK_LEVEL = 40;

/** Unit level of a pack's zombies: ≈ 3 × pack level (40-combat.md R34). */
export function packUnitLevel(packLevel: number): number {
  return Math.max(1, 3 * packLevel);
}

const TIER_WEIGHT: Record<string, number> = { D: 1, C: 1.15, B: 1.35, A: 1.6, S: 2 };

function estimatePower(members: PackMember[]): number {
  let sum = 0;
  for (const m of members) {
    const z = ZOMBIES.find((x) => x.id === m.zombieId);
    const tier = TIER_WEIGHT[z?.tier ?? "D"] ?? 1;
    const hpMult = z?.statMult.hp ?? 1;
    sum += Math.round(120 * tier * hpMult * (1 + 0.06 * (m.level - 1)));
  }
  return sum;
}

const member = (zombieId: string, level: number, slot: PackSlot = "fighter"): PackMember => ({ zombieId, level, slot });

/** Composition per threat band; later bands add elites, supports and a commander (R34). */
function composition(level: number): PackMember[] {
  const L = packUnitLevel(level);
  if (level <= 5) {
    const m = [member("zmb.shambler", L), member("zmb.shambler", L)];
    if (level >= 2) m.push(member("zmb.runner", L));
    if (level >= 4) m.push(member("zmb.shambler", L));
    return m;
  }
  if (level <= 12) {
    return [member("zmb.shambler", L), member("zmb.bloater", L), member("zmb.spitter", L), member(level >= 9 ? "zmb.runner" : "zmb.shambler", L)];
  }
  if (level <= 20) {
    return [member("zmb.brute", L), member("zmb.crawler", L), member("zmb.spitter", L), member("zmb.runner", L), member("zmb.screamer", L, "support")];
  }
  if (level <= 30) {
    const m = [member("zmb.hulk", L), member("zmb.stalker", L), member("zmb.spitter", L), member("zmb.crawler", L), member("zmb.carrier", L, "support")];
    if (level >= 25) m.unshift(member("zmb.necromancer", L, "commander"));
    return m;
  }
  return [
    member("zmb.necromancer", L, "commander"),
    member("zmb.abomination", L),
    member("zmb.hulk", L),
    member("zmb.stalker", L),
    member("zmb.bloater", L),
    member("zmb.carrier", L, "support"),
    member("zmb.screamer", L, "support"),
  ];
}

const packCache = new Map<number, PackTemplate>();

/** Zombie pack for a world tile / expedition stage at `level` 1..40. */
export function packTemplate(level: number): PackTemplate {
  const lv = Math.max(1, Math.min(MAX_PACK_LEVEL, Math.round(level)));
  const cached = packCache.get(lv);
  if (cached) return cached;
  const members = composition(lv);
  const t: PackTemplate = { level: lv, kind: "pack", members, power: estimatePower(members) };
  packCache.set(lv, t);
  return t;
}

/** Hive levels 1..10: the Matriarch plus escorts; her HP persists between attacks within a day (R34). */
export function hiveTemplate(level: number): PackTemplate {
  const lv = Math.max(1, Math.min(10, Math.round(level)));
  const L = packUnitLevel(10 + 3 * lv);
  const members: PackMember[] = [
    member("zmb.boss_hive_matriarch", L, "commander"),
    member("zmb.brute", L),
    member("zmb.spitter", L),
    member(lv >= 4 ? "zmb.hulk" : "zmb.bloater", L),
    member(lv >= 7 ? "zmb.abomination" : "zmb.stalker", L),
    member("zmb.carrier", L, "support"),
  ];
  if (lv >= 5) members.push(member("zmb.screamer", L, "support"));
  return { level: lv, kind: "hive", members, power: estimatePower(members) };
}

/** The world boss: Titan Necrolith with an elite escort at pack level 40. */
export function worldBossTemplate(): PackTemplate {
  const L = packUnitLevel(MAX_PACK_LEVEL);
  const members: PackMember[] = [
    member("zmb.necromancer", L, "commander"),
    member("zmb.boss_titan_necrolith", L),
    member("zmb.hulk", L),
    member("zmb.hulk", L),
    member("zmb.abomination", L),
    member("zmb.carrier", L, "support"),
    member("zmb.carrier", L, "support"),
  ];
  return { level: MAX_PACK_LEVEL, kind: "boss", members, power: estimatePower(members) };
}

/** R37: the tenth and twentieth siege waves contain actual bosses at the wave's scale. */
export function siegeWaveTemplate(hqLevel: number, wave: number): PackTemplate {
  const base = packTemplate(hqLevel + wave - 1);
  if (wave !== 10 && wave !== 20) return base;
  const level = packUnitLevel(base.level);
  const boss = wave === 10 ? member("zmb.boss_hive_matriarch", level, "commander") : member("zmb.boss_titan_necrolith", level);
  const members = wave === 10 ? [boss, ...base.members.filter((unit) => unit.slot !== "commander")] : [...base.members.filter((unit, index) => unit.slot !== "fighter" || index !== base.members.findIndex((entry) => entry.slot === "fighter")), boss];
  return { level: base.level, kind: "boss", members, power: estimatePower(members) };
}

const ZONE_LEVEL: Record<string, number> = { north: 6, east: 12, south: 20, west: 30 };

/** Necro Nest garrison defending a base sector before it can be reclaimed (20-base.md R13). */
export function zoneGarrison(zoneId: string): PackTemplate {
  const level = ZONE_LEVEL[zoneId];
  if (level === undefined) throw new Error(`unknown zone ${zoneId}`);
  const base = packTemplate(level);
  const L = packUnitLevel(level);
  const members = [...base.members.filter((m) => m.slot !== "support"), member("zmb.screamer", L, "support")];
  return { level, kind: "zone", members, power: estimatePower(members) };
}
